import {
  Avatar,
  Body,
  Button,
  Card,
    Divider,
  I,
  List,
  Pill,
  Pullquote,
  Row,
  Stack,
} from '@leanin/ui';
import type { MembershipRequestView } from '../../../lib/queries/groups';

// The pending-join-request list card. Identical UI for circles and
// networks pre-extraction — this is the consolidated home. Owns no
// mutations: the caller passes onApprove / onDecline and the pending
// flag, and the card renders + invokes.
//
// Rows are the `direction='request'` slice of the unified membership-request
// view; each row's `subject` is the requester's profile snapshot.

type Props = {
  readonly requests: ReadonlyArray<MembershipRequestView>;
  readonly onApprove: (requestId: string) => void;
  readonly onDecline: (requestId: string) => void;
  readonly onMessage: (userId: string) => void;
  readonly isPending: boolean;
};

function memberSublabel(
  m: { jobTitle: string | null; company: string | null } | null,
): string | null {
  if (m === null) return null;
  return [m.jobTitle, m.company].filter(Boolean).join(' · ') || null;
}

function formatRelative(iso: string): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return '';
  const diffMs = Date.now() - then;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.round(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export function AdminJoinRequestsCard({
  requests,
  onApprove,
  onDecline,
  onMessage,
  isPending,
}: Props) {
  if (requests.length === 0) {
    return (
      <Card pad="flush">
        <Row p="xl" justify="center">
          <Body size="sm" color="mute">
            No pending join requests. Check back later.
          </Body>
        </Row>
      </Card>
    );
  }

  return (
    <Card pad="flush">
      <List bordered={false}>
        {requests.map((req) => {
          const subject = req.subject;
          const fullName =
            `${subject.firstName ?? ''} ${subject.lastName ?? ''}`.trim() ||
            'Unknown';
          // A `request` row always has an on-platform subject; `subject.userId`
          // is the requester. Guard for the type, defaulting to '' so the
          // Message CTA degrades rather than crashes on a malformed row.
          const subjectUserId = subject.userId ?? '';
          return (
            <Row key={req.id} gap="lg" align="flex-start" wrap p="xl">
              <Row
                gap="md"
                align="flex-start"
                style={{ flex: '2 1 360px', minWidth: 0 }}
              >
                <Avatar
                  src={subject.photoUrl ?? undefined}
                  name={fullName}
                  size={44}
                />
                <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
                  <Stack gap="xs">
                    <Body size="md">{fullName}</Body>
                    <Row gap="xs" align="center" wrap>
                      <Body size="sm" color="mute">
                        {memberSublabel(subject) ?? 'Member'}
                      </Body>
                      <Body size="sm" color="mute">
                        · Requested {formatRelative(req.createdAt)}
                      </Body>
                    </Row>
                  </Stack>
                  {req.message !== null && req.message.length > 0 ? (
                    <Pullquote tone="neutral">{req.message}</Pullquote>
                  ) : null}
                </Stack>
              </Row>

              <Divider vertical />

              <Stack
                gap="sm"
                ps="md"
                style={{ flex: '1 1 200px', minWidth: 200 }}
              >
                <Pill tone="neutral">Awaiting review</Pill>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={I.mail}
                  block
                  onClick={() => onMessage(subjectUserId)}
                  disabled={isPending}
                >
                  Message {fullName.split(' ')[0]}
                </Button>
                <Row gap="sm">
                  <Button
                    variant="primary"
                    size="sm"
                    icon={I.check}
                    style={{ flex: 1 }}
                    onClick={() => onApprove(req.id)}
                    disabled={isPending}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="ghostDanger"
                    size="sm"
                    style={{ flex: 1 }}
                    onClick={() => onDecline(req.id)}
                    disabled={isPending}
                  >
                    Decline
                  </Button>
                </Row>
              </Stack>
            </Row>
          );
        })}
      </List>
    </Card>
  );
}
